/* eslint-disable react-hooks/exhaustive-deps */
/**
 * #185 — Lead-detail Overview "Recent attachments" mini-list.
 *
 * Pulls the most recent 3-5 LeadMedia rows for the lead and renders a compact
 * card with a "View all →" link to the Documents tab. Used on the Overview
 * tab so users have one-click access to recent files without hunting through
 * the Documents tab.
 */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Box, Card, Stack, Typography, IconButton, Tooltip, Skeleton, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { callApi } from 'src/utils/apiCall';
import { primary, grey } from 'src/theme/palette';
import { Iconify } from '.';
import { getAttachmentLabel, getAttachmentUrl, isChatbotPhoto, isSafeImageUrl } from './attachmentsUtils';

const MAX_ITEMS = 5;

function fmtDate(s) {
    if (!s) return '—';
    try {
        return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '—';
    }
}

export default function RecentAttachments({ practiceId, leadId, onViewAll }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [refreshTick, setRefreshTick] = useState(0);
    const imageRetried = useRef(false);

    // Signed photo URLs expire: on the first <img> error re-fetch the list once for fresh URLs.
    const handleImageError = () => {
        if (imageRetried.current) return;
        imageRetried.current = true;
        setRefreshTick((n) => n + 1);
    };

    useEffect(() => {
        if (!practiceId || !leadId) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads-media/?lead__in=${leadId}&limit=${MAX_ITEMS}&offset=0&ordering=-created_at`,
                    'GET',
                    null,
                    false,
                    refreshTick > 0 ? { bustCache: true } : {}
                );
                if (!cancelled && res?.status === 200) {
                    setItems(res.data?.results || []);
                    setTotal(res.data?.count || 0);
                }
            } catch (err) {
                Sentry.captureException(err, { tags: { context: 'recent_attachments_fetch' } });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [practiceId, leadId, refreshTick]);

    // Hide entirely while loading the first time (don't flash an empty card)
    // unless we already know there are zero — in which case skip render too.
    if (!loading && items.length === 0) return null;

    return (
        <Card sx={{ p: 2, mt: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="eva:attach-2-fill" width={18} sx={{ color: primary.main }} />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                        Recent attachments{total > 0 ? ` (${total})` : ''}
                    </Typography>
                </Stack>
                {onViewAll && total > items.length && (
                    <Button
                        size="small"
                        variant="text"
                        endIcon={<Iconify icon="eva:arrow-forward-fill" width={14} />}
                        onClick={onViewAll}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        View all
                    </Button>
                )}
            </Stack>

            {loading && items.length === 0 && (
                <Stack spacing={0.75}>
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} variant="rounded" height={36} />
                    ))}
                </Stack>
            )}

            {items.length > 0 && (
                <Stack spacing={0.75}>
                    {items.map((it) => {
                        const filename = getAttachmentLabel(it);
                        const rawUrl = getAttachmentUrl(it);
                        const url = isSafeImageUrl(rawUrl) ? rawUrl : null;
                        const isPhoto = isChatbotPhoto(it);
                        return (
                            <Box
                                key={it.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    '&:hover': { borderColor: 'primary.light', bgcolor: alpha(primary.main, 0.02) },
                                }}
                            >
                                {isPhoto ? (
                                    <Box
                                        component="img"
                                        src={url}
                                        alt={filename}
                                        onError={handleImageError}
                                        sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
                                    />
                                ) : (
                                    <Iconify icon="eva:file-text-outline" width={16} sx={{ color: grey[500], flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.78rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {filename}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                                        {fmtDate(it.created_at)}
                                    </Typography>
                                </Box>
                                {url && (
                                    <Tooltip title="Open">
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: primary.main, flexShrink: 0 }}
                                        >
                                            <Iconify icon="eva:external-link-outline" width={16} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Card>
    );
}

RecentAttachments.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onViewAll: PropTypes.func,
};

RecentAttachments.defaultProps = {
    onViewAll: undefined,
};
